import classNames from 'classnames';
import type { HTMLAttributes, KeyboardEvent } from 'react';
import styles from './drop-target.module.scss';

interface DropTargetState {
    /** Whether the frame can be clicked or dropped on. */
    isTarget: boolean;
    /** Marks the frame as a valid place for the selected item. */
    isHighlighted: boolean;
    /** Marks the frame as holding the selected item. */
    isSelected: boolean;
}

/** Visual states for a frame that can receive an item. */
export function resolveDropTargetClassName({ isTarget, isHighlighted, isSelected }: DropTargetState): string {
    return classNames(isTarget && styles.dropTarget, isHighlighted && styles.highlighted, isSelected && styles.selected);
}

/**
 * Props that make a frame a click/keyboard target (selecting what it holds).
 * A dragged item is placed through `useDropTarget` instead. With no handler
 * the frame only shows its item: a named group around it, so it is still
 * announced (a plain div can't carry a name) and what it holds can stay
 * interactive.
 */
export function getDropTargetProps(onActivate: (() => void) | undefined, ariaLabel: string): HTMLAttributes<HTMLDivElement> {
    if (onActivate === undefined) {
        return { role: 'group', 'aria-label': ariaLabel };
    }

    return {
        role: 'button',
        tabIndex: 0,
        'aria-label': ariaLabel,
        onClick: () => onActivate(),
        onKeyDown: (event: KeyboardEvent<HTMLDivElement>): void => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onActivate();
            }
        },
    };
}
